import React, { useCallback } from "react";
import i18next from "i18next";
import {
  Box,
  FormHelperText,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { dt } from "../../../themes/redesignTokens";
import { useFormFileUpload, UploadedFile } from "./useFormFileUpload";

export interface FormFileUploadProps {
  files: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  maxFileSizeMb?: number;
  accept?: string;
  multiple?: boolean;
  dropZoneHeight?: string;
  labels?: {
    dragAndDrop?: string;
    supportedFormats?: string;
    fileTooLarge?: string;
    delete?: string;
  };
}

// ── File helpers (from SR/RFS) ────────────────────
const getFileIcon = (name: string): React.ReactNode => {
  const ext = name.toLowerCase().split(".").pop();
  if (ext === "pdf")
    return <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: "#E74C3C" }} />;
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext || ""))
    return <ImageOutlinedIcon sx={{ fontSize: 18, color: dt.blue }} />;
  return (
    <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />
  );
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Drag-and-drop file upload zone with file list and preview.
 *
 * Extracted from SR/RFS (identical implementation in both, proven production pattern).
 * Handles drag-over, drop, click-to-upload, file removal, size validation, and file type icons.
 */
const FormFileUpload: React.FC<FormFileUploadProps> = ({
  files,
  onFilesChange,
  maxFileSizeMb = 10,
  accept,
  multiple = true,
  dropZoneHeight = "100px",
  labels,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    isDragOver,
    fileError,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    processFiles,
  } = useFormFileUpload({
    maxFileSizeMb,
  });

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleDragLeave(e);
      if (e.dataTransfer.files.length > 0) {
        const newFiles = await processFiles(Array.from(e.dataTransfer.files));
        if (newFiles.length > 0) {
          onFilesChange([...files, ...newFiles]);
        }
      }
    },
    [files, onFilesChange, processFiles, handleDragLeave]
  );

  const handleInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const newFiles = await processFiles(Array.from(e.target.files));
        if (newFiles.length > 0) {
          onFilesChange([...files, ...newFiles]);
        }
      }
      e.target.value = "";
    },
    [files, onFilesChange, processFiles]
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      onFilesChange(files.filter((_, i) => i !== index));
    },
    [files, onFilesChange]
  );

  const dragDropLabel = labels?.dragAndDrop || "Drag & drop files here, or click to browse";
  const formatsLabel = labels?.supportedFormats || "PDF, images, documents up to 10MB";
  const deleteLabel = labels?.delete || (i18next.t("common:buttons.delete") as string);

  return (
    <Box>
      {/* Drop zone */}
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          height: dropZoneHeight,
          border: `2px dashed ${
            fileError ? "#C0392B" : isDragOver ? dt.blue : dt.cardBorder
          }`,
          borderRadius: dt.radiusSm,
          backgroundColor: isDragOver ? dt.blueGlow : dt.inputBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: dt.blue,
            backgroundColor: dt.blueSoft,
          },
        }}
      >
        <CloudUploadOutlinedIcon
          sx={{
            fontSize: 28,
            color: isDragOver ? dt.blue : dt.textMuted,
            transition: "color 0.2s ease",
          }}
        />
        <Typography
          sx={{ fontSize: "13px", color: dt.textSecondary, fontFamily: dt.font }}
        >
          {dragDropLabel}
        </Typography>
        <Box
          sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font }}
        >
          {formatsLabel}
        </Box>
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        style={{ display: "none" }}
        onChange={handleInputChange}
      />

      {/* Error message */}
      {fileError && (
        <FormHelperText error sx={{ mt: 0.5, fontFamily: dt.font }}>
          {fileError}
        </FormHelperText>
      )}

      {/* File list */}
      {files.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          {files.map((file, index) => (
            <Box
              key={`${file.name}-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1,
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                backgroundColor: dt.cardBg,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#FAFAFA",
                  "& .file-delete": { opacity: 1 },
                },
                animation: `fadeUp 0.3s ease ${index * 0.05}s both`,
              }}
            >
              {getFileIcon(file.name)}
              <Typography
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 500,
                  color: dt.textPrimary,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {file.name}
              </Typography>
              {file.size > 0 && (
                <Typography
                  sx={{
                    fontFamily: dt.fontMono,
                    fontSize: "11px",
                    fontWeight: 500,
                    color: dt.textMuted,
                    flexShrink: 0,
                  }}
                >
                  {formatFileSize(file.size)}
                </Typography>
              )}
              <Tooltip title={deleteLabel}>
                <IconButton
                  className="file-delete"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile(index);
                  }}
                  sx={{
                    opacity: 0.5,
                    transition: "opacity 0.2s ease",
                    p: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(220,38,38,0.08)",
                      color: "#C0392B",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FormFileUpload;
